import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Palette, Upload, Image, FileImage, Check, AlertCircle, Download, Layers, Sparkles } from 'lucide-react';
import axiosInstance from '../../../config/axios';

interface DesignType {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
}

interface DesignSize {
  id: string;
  name: string;
  width: number;
  height: number;
  platform?: string;
}

interface BrandGuideline {
  id: string;
  type: 'color' | 'font' | 'logo' | 'spacing' | 'imagery';
  value: string;
  compliant: boolean | null;
}

interface DesignFile {
  id: string;
  file: File;
  preview: string;
  size: DesignSize;
  format: string;
}

const DesignAssetCreation: React.FC = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedType, setSelectedType] = useState<DesignType | null>(null);
  const [designName, setDesignName] = useState('');
  const [designDescription, setDesignDescription] = useState('');

  // Design-specific data
  const [selectedSizes, setSelectedSizes] = useState<DesignSize[]>([]);
  const [designFiles, setDesignFiles] = useState<DesignFile[]>([]);
  const [sourceFiles, setSourceFiles] = useState<File[]>([]);
  const [brandGuidelines, setBrandGuidelines] = useState<BrandGuideline[]>([
    { id: '1', type: 'color', value: 'Primary brand colors used', compliant: null },
    { id: '2', type: 'font', value: 'Approved typefaces used', compliant: null },
    { id: '3', type: 'logo', value: 'Logo usage follows guidelines', compliant: null },
    { id: '4', type: 'spacing', value: 'Proper spacing and margins', compliant: null },
    { id: '5', type: 'imagery', value: 'Image style matches brand', compliant: null },
  ]);
  const [designNotes, setDesignNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const designTypes: DesignType[] = [
    {
      id: 'logo',
      name: 'Logo Design',
      description: 'Brand logos, variations, and lockups',
      icon: <Sparkles className="w-6 h-6" />,
    },
    {
      id: 'logo_contest',
      name: 'Logo Contest',
      description: 'Multiple logo variations for review and selection',
      icon: <Layers className="w-6 h-6" />,
    },
    {
      id: 'social_graphics',
      name: 'Social Media Graphics',
      description: 'Platform-specific social media designs',
      icon: <Image className="w-6 h-6" />,
    },
    {
      id: 'banner_ads',
      name: 'Display Banners',
      description: 'Web banners and display ads',
      icon: <Layers className="w-6 h-6" />,
    },
    {
      id: 'print',
      name: 'Print Materials',
      description: 'Brochures, flyers, business cards',
      icon: <FileImage className="w-6 h-6" />,
    },
    {
      id: 'web_graphics',
      name: 'Web Graphics',
      description: 'Website graphics, icons, illustrations',
      icon: <Palette className="w-6 h-6" />,
    },
  ];

  const logoSizes: DesignSize[] = [
    { id: 'primary', name: 'Primary Logo', width: 2000, height: 2000 },
    { id: 'horizontal', name: 'Horizontal', width: 3000, height: 1000 },
    { id: 'stacked', name: 'Stacked', width: 1000, height: 1500 },
    { id: 'icon', name: 'Icon/Symbol', width: 1000, height: 1000 },
    { id: 'favicon', name: 'Favicon', width: 512, height: 512 },
  ];

  const logoContestSizes: DesignSize[] = [
    { id: 'variation_1', name: 'Logo Variation 1', width: 2000, height: 2000 },
    { id: 'variation_2', name: 'Logo Variation 2', width: 2000, height: 2000 },
    { id: 'variation_3', name: 'Logo Variation 3', width: 2000, height: 2000 },
    { id: 'variation_4', name: 'Logo Variation 4', width: 2000, height: 2000 },
    { id: 'variation_5', name: 'Logo Variation 5', width: 2000, height: 2000 },
    { id: 'variation_more', name: 'Additional Variations', width: 2000, height: 2000 },
  ];

  const socialGraphicSizes: DesignSize[] = [
    { id: 'ig_post', name: 'Instagram Post', width: 1080, height: 1080, platform: 'Instagram' },
    { id: 'ig_story', name: 'Instagram Story', width: 1080, height: 1920, platform: 'Instagram' },
    { id: 'fb_post', name: 'Facebook Post', width: 1200, height: 630, platform: 'Facebook' },
    { id: 'fb_cover', name: 'Facebook Cover', width: 820, height: 312, platform: 'Facebook' },
    { id: 'twitter_post', name: 'Twitter Post', width: 1200, height: 675, platform: 'Twitter' },
    { id: 'twitter_header', name: 'Twitter Header', width: 1500, height: 500, platform: 'Twitter' },
    { id: 'linkedin_post', name: 'LinkedIn Post', width: 1200, height: 627, platform: 'LinkedIn' },
    { id: 'linkedin_cover', name: 'LinkedIn Cover', width: 1584, height: 396, platform: 'LinkedIn' },
  ];

  const bannerSizes: DesignSize[] = [
    { id: 'leaderboard', name: 'Leaderboard', width: 728, height: 90 },
    { id: 'banner', name: 'Banner', width: 468, height: 60 },
    { id: 'skyscraper', name: 'Skyscraper', width: 160, height: 600 },
    { id: 'rectangle', name: 'Medium Rectangle', width: 300, height: 250 },
    { id: 'large_rectangle', name: 'Large Rectangle', width: 336, height: 280 },
    { id: 'mobile', name: 'Mobile Banner', width: 320, height: 50 },
  ];

  const printSizes: DesignSize[] = [
    { id: 'a4', name: 'A4 (210×297mm)', width: 2480, height: 3508 },
    { id: 'letter', name: 'Letter (8.5×11")', width: 2550, height: 3300 },
    { id: 'business_card', name: 'Business Card (3.5×2")', width: 1050, height: 600 },
    { id: 'postcard', name: 'Postcard (6×4")', width: 1800, height: 1200 },
    { id: 'flyer', name: 'Flyer (8.5×11")', width: 2550, height: 3300 },
  ];

  const webGraphicSizes: DesignSize[] = [
    { id: 'hero', name: 'Hero Image', width: 1920, height: 1080 },
    { id: 'thumbnail', name: 'Thumbnail', width: 400, height: 300 },
    { id: 'icon_large', name: 'Icon (Large)', width: 512, height: 512 },
    { id: 'icon_small', name: 'Icon (Small)', width: 128, height: 128 },
  ];

  const getSizesForType = (): DesignSize[] => {
    switch (selectedType?.id) {
      case 'logo':
        return logoSizes;
      case 'logo_contest':
        return logoContestSizes;
      case 'social_graphics':
        return socialGraphicSizes;
      case 'banner_ads':
        return bannerSizes;
      case 'print':
        return printSizes;
      case 'web_graphics':
        return webGraphicSizes;
      default:
        return [];
    }
  };

  const fileFormats = {
    logo: [
      { ext: 'AI', name: 'Adobe Illustrator', recommended: true },
      { ext: 'EPS', name: 'Vector EPS', recommended: true },
      { ext: 'SVG', name: 'SVG Vector', recommended: true },
      { ext: 'PDF', name: 'PDF Vector', recommended: false },
      { ext: 'PNG', name: 'PNG (Transparent)', recommended: true },
      { ext: 'JPG', name: 'JPEG', recommended: false },
    ],
    logo_contest: [
      { ext: 'PNG', name: 'PNG (Transparent)', recommended: true },
      { ext: 'SVG', name: 'SVG Vector', recommended: true },
      { ext: 'AI', name: 'Adobe Illustrator', recommended: true },
      { ext: 'EPS', name: 'Vector EPS', recommended: false },
      { ext: 'JPG', name: 'JPEG', recommended: false },
    ],
    social_graphics: [
      { ext: 'PSD', name: 'Photoshop', recommended: true },
      { ext: 'AI', name: 'Illustrator', recommended: true },
      { ext: 'PNG', name: 'PNG', recommended: true },
      { ext: 'JPG', name: 'JPEG', recommended: true },
    ],
    banner_ads: [
      { ext: 'PSD', name: 'Photoshop', recommended: true },
      { ext: 'HTML', name: 'HTML5', recommended: true },
      { ext: 'GIF', name: 'Animated GIF', recommended: false },
      { ext: 'PNG', name: 'PNG', recommended: true },
      { ext: 'JPG', name: 'JPEG', recommended: true },
    ],
    print: [
      { ext: 'AI', name: 'Illustrator', recommended: true },
      { ext: 'INDD', name: 'InDesign', recommended: true },
      { ext: 'PDF', name: 'Print PDF', recommended: true },
      { ext: 'PSD', name: 'Photoshop', recommended: false },
    ],
    web_graphics: [
      { ext: 'PSD', name: 'Photoshop', recommended: true },
      { ext: 'AI', name: 'Illustrator', recommended: true },
      { ext: 'SVG', name: 'SVG', recommended: true },
      { ext: 'PNG', name: 'PNG', recommended: true },
      { ext: 'JPG', name: 'JPEG', recommended: true },
    ],
  };

  const getFileFormats = () => {
    return fileFormats[selectedType?.id as keyof typeof fileFormats] || [];
  };

  const handleDesignFileUpload = (e: React.ChangeEvent<HTMLInputElement>, size: DesignSize) => {
    const files = Array.from(e.target.files || []);

    files.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const newDesignFile: DesignFile = {
          id: Date.now().toString() + Math.random(),
          file,
          preview: event.target?.result as string,
          size,
          format: file.name.split('.').pop()?.toUpperCase() || 'Unknown',
        };
        setDesignFiles((prev) => [...prev, newDesignFile]);
      };
      reader.readAsDataURL(file);
    });
  };

  const handleSourceFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setSourceFiles((prev) => [...prev, ...files]);
  };

  const removeDesignFile = (id: string) => {
    setDesignFiles((prev) => prev.filter((f) => f.id !== id));
  };

  const removeSourceFile = (index: number) => {
    setSourceFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const toggleSize = (size: DesignSize) => {
    if (selectedSizes.find((s) => s.id === size.id)) {
      setSelectedSizes((prev) => prev.filter((s) => s.id !== size.id));
    } else {
      setSelectedSizes((prev) => [...prev, size]);
    }
  };

  const toggleBrandCompliance = (id: string, compliant: boolean) => {
    setBrandGuidelines((prev) =>
      prev.map((g) => (g.id === id ? { ...g, compliant } : g))
    );
  };

  const getBrandComplianceScore = () => {
    const total = brandGuidelines.length;
    const compliant = brandGuidelines.filter((g) => g.compliant === true).length;
    return total > 0 ? Math.round((compliant / total) * 100) : 0;
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      // Step 1: Create the content item
      const contentPayload = {
        type: 'design',
        title: designName,
        description: designDescription,
        status: 'draft',
        metadata: {
          design_type: selectedType?.id || 'graphic',
          selected_sizes: selectedSizes,
          brand_compliance_score: getBrandComplianceScore(),
          design_notes: designNotes,
          file_formats: designFiles.map(df => df.format),
          variations_count: designFiles.length,
        }
      };

      const contentResponse = await axiosInstance.post('/content', contentPayload);
      const contentId = contentResponse.data.id;

      if (!contentId) {
        throw new Error('Failed to create content item');
      }

      // Step 2: Upload all design files as assets
      const uploadPromises = designFiles.map(async (df) => {
        const formData = new FormData();
        formData.append('file', df.file);
        formData.append('content_id', contentId.toString());
        formData.append('asset_type', selectedType?.id === 'logo_contest' ? 'logo_contest' : 'image');
        formData.append('file_name', df.file.name);

        // Add metadata for the asset
        const assetMetadata = {
          size: df.size.name,
          dimensions: `${df.size.width}x${df.size.height}`,
          format: df.format,
        };
        formData.append('metadata', JSON.stringify(assetMetadata));

        return axiosInstance.post('/assets/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
      });

      // Step 3: Upload source files as supplementary assets
      const sourceUploadPromises = sourceFiles.map(async (sf) => {
        const formData = new FormData();
        formData.append('file', sf);
        formData.append('content_id', contentId.toString());
        formData.append('asset_type', 'source_file');
        formData.append('file_name', sf.name);
        formData.append('is_supplementary', 'true');

        return axiosInstance.post('/assets/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
      });

      // Wait for all uploads to complete
      await Promise.all([...uploadPromises, ...sourceUploadPromises]);

      // Success! Redirect to design management page
      navigate('/dashboard/design');
    } catch (error) {
      console.error('Error creating design assets:', error);
      alert('Failed to create design assets. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const canProceedToNextStep = () => {
    switch (currentStep) {
      case 1:
        return selectedType !== null;
      case 2:
        return designName.trim() !== '' && selectedSizes.length > 0;
      case 3:
        return designFiles.length > 0;
      case 4:
        return true;
      default:
        return false;
    }
  };

  return (
    <div className="max-w-5xl mx-auto p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
          <Palette className="w-8 h-8 text-purple-600" />
          Create Design Assets
        </h1>
        <p className="text-gray-600 mt-2">
          Upload and organize design files for logos, social graphics, banners, and more
        </p>
      </div>

      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {['Design Type', 'Configuration', 'Upload Files', 'Preview & Submit'].map((label, index) => {
            const stepNumber = index + 1;
            const isActive = currentStep === stepNumber;
            const isCompleted = currentStep > stepNumber;

            return (
              <div key={stepNumber} className="flex items-center flex-1">
                <div className="flex flex-col items-center flex-1">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                      isCompleted
                        ? 'bg-green-600 text-white'
                        : isActive
                        ? 'bg-purple-600 text-white'
                        : 'bg-gray-300 text-gray-600'
                    }`}
                  >
                    {isCompleted ? <Check className="w-5 h-5" /> : stepNumber}
                  </div>
                  <span className="text-xs mt-2 text-gray-700">{label}</span>
                </div>
                {stepNumber < 4 && (
                  <div
                    className={`h-1 flex-1 ${
                      currentStep > stepNumber ? 'bg-green-600' : 'bg-gray-300'
                    }`}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Step 1: Design Type Selection */}
      {currentStep === 1 && (
        <div className="space-y-4">
          <h2 className="text-xl font-semibold text-gray-800">Select Design Type</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {designTypes.map((type) => (
              <button
                key={type.id}
                onClick={() => setSelectedType(type)}
                className={`p-6 rounded-lg border-2 text-left transition-all ${
                  selectedType?.id === type.id
                    ? 'border-purple-600 bg-purple-50'
                    : 'border-gray-200 hover:border-purple-300 bg-white'
                }`}
              >
                <div className="flex items-start gap-4">
                  <div className="text-purple-600">{type.icon}</div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900">{type.name}</h3>
                    <p className="text-sm text-gray-600 mt-1">{type.description}</p>
                  </div>
                  {selectedType?.id === type.id && (
                    <Check className="w-6 h-6 text-purple-600" />
                  )}
                </div>
              </button>
            ))}
          </div>

          {selectedType && (
            <div className="mt-6 p-4 bg-purple-50 border border-purple-200 rounded-lg">
              <h4 className="font-semibold text-purple-900">File Formats for {selectedType.name}</h4>
              <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-2">
                {getFileFormats().map((format) => (
                  <div key={format.ext} className="flex items-center gap-2">
                    <span className="text-sm font-mono bg-white px-2 py-1 rounded border border-purple-200">
                      .{format.ext}
                    </span>
                    <span className="text-xs text-gray-700">{format.name}</span>
                    {format.recommended && (
                      <span className="text-xs bg-green-100 text-green-700 px-1 rounded">★</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Step 2: Configuration */}
      {currentStep === 2 && (
        <div className="space-y-6">
          <h2 className="text-xl font-semibold text-gray-800">Design Configuration</h2>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Design Name *
            </label>
            <input
              type="text"
              value={designName}
              onChange={(e) => setDesignName(e.target.value)}
              placeholder="e.g., ABC Company Logo Package Q1 2024"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={designDescription}
              onChange={(e) => setDesignDescription(e.target.value)}
              rows={3}
              placeholder="Brief description of the design project..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Select Required Sizes * ({selectedSizes.length} selected)
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-96 overflow-y-auto p-2 border border-gray-200 rounded-lg">
              {getSizesForType().map((size) => {
                const isSelected = selectedSizes.find((s) => s.id === size.id);
                return (
                  <button
                    key={size.id}
                    onClick={() => toggleSize(size)}
                    className={`p-4 rounded-lg border-2 text-left transition-all ${
                      isSelected
                        ? 'border-purple-600 bg-purple-50'
                        : 'border-gray-200 hover:border-purple-300 bg-white'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-semibold text-gray-900">{size.name}</div>
                        <div className="text-sm text-gray-600">
                          {size.width} × {size.height}px
                        </div>
                        {size.platform && (
                          <div className="text-xs text-purple-600 mt-1">{size.platform}</div>
                        )}
                      </div>
                      {isSelected && <Check className="w-5 h-5 text-purple-600" />}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Step 3: Upload Files */}
      {currentStep === 3 && (
        <div className="space-y-6">
          <h2 className="text-xl font-semibold text-gray-800">Upload Design Files</h2>

          {/* Upload for each selected size */}
          <div className="space-y-4">
            <h3 className="font-medium text-gray-700">Design Files (by Size)</h3>
            {selectedSizes.map((size) => {
              const filesForSize = designFiles.filter((f) => f.size.id === size.id);

              return (
                <div key={size.id} className="border border-gray-300 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <div className="font-semibold text-gray-900">{size.name}</div>
                      <div className="text-sm text-gray-600">
                        {size.width} × {size.height}px
                      </div>
                    </div>
                    <label className="cursor-pointer px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm">
                      <Upload className="w-4 h-4 inline mr-2" />
                      Upload
                      <input
                        type="file"
                        multiple
                        accept="image/*,.ai,.eps,.svg,.psd,.pdf"
                        onChange={(e) => handleDesignFileUpload(e, size)}
                        className="hidden"
                      />
                    </label>
                  </div>

                  {filesForSize.length > 0 && (
                    <div className="grid grid-cols-3 md:grid-cols-4 gap-3 mt-3">
                      {filesForSize.map((file) => (
                        <div key={file.id} className="relative group">
                          {file.file.type.startsWith('image/') ? (
                            <img
                              src={file.preview}
                              alt={file.file.name}
                              className="w-full h-24 object-cover rounded border border-gray-200"
                            />
                          ) : (
                            <div className="w-full h-24 bg-gray-100 rounded border border-gray-200 flex items-center justify-center">
                              <FileImage className="w-8 h-8 text-gray-400" />
                            </div>
                          )}
                          <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={() => removeDesignFile(file.id)}
                              className="bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-700"
                            >
                              ×
                            </button>
                          </div>
                          <div className="text-xs text-gray-600 mt-1 truncate">
                            {file.file.name}
                          </div>
                          <div className="text-xs text-purple-600 font-semibold">
                            {file.format}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Source Files */}
          <div className="border border-gray-300 rounded-lg p-4">
            <h3 className="font-medium text-gray-700 mb-3">Source Files (AI, PSD, etc.)</h3>
            <p className="text-sm text-gray-600 mb-3">
              Upload original working files for future edits
            </p>
            <label className="cursor-pointer px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm inline-block">
              <Upload className="w-4 h-4 inline mr-2" />
              Upload Source Files
              <input
                type="file"
                multiple
                accept=".ai,.psd,.eps,.indd,.sketch,.fig,.xd"
                onChange={handleSourceFileUpload}
                className="hidden"
              />
            </label>

            {sourceFiles.length > 0 && (
              <div className="mt-4 space-y-2">
                {sourceFiles.map((file, index) => (
                  <div key={index} className="flex items-center justify-between bg-gray-50 p-3 rounded">
                    <div className="flex items-center gap-3">
                      <FileImage className="w-5 h-5 text-gray-500" />
                      <div>
                        <div className="text-sm font-medium text-gray-900">{file.name}</div>
                        <div className="text-xs text-gray-500">
                          {(file.size / 1024 / 1024).toFixed(2)} MB
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => removeSourceFile(index)}
                      className="text-red-600 hover:text-red-800"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Step 4: Preview & Brand Compliance */}
      {currentStep === 4 && (
        <div className="space-y-6">
          <h2 className="text-xl font-semibold text-gray-800">Preview & Brand Compliance</h2>

          {/* Summary */}
          <div className="bg-white border border-gray-300 rounded-lg p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Design Summary</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-sm text-gray-600">Design Type</div>
                <div className="font-semibold">{selectedType?.name}</div>
              </div>
              <div>
                <div className="text-sm text-gray-600">Design Name</div>
                <div className="font-semibold">{designName}</div>
              </div>
              <div>
                <div className="text-sm text-gray-600">Sizes</div>
                <div className="font-semibold">{selectedSizes.length} variants</div>
              </div>
              <div>
                <div className="text-sm text-gray-600">Total Files</div>
                <div className="font-semibold">
                  {designFiles.length} design files, {sourceFiles.length} source files
                </div>
              </div>
            </div>
          </div>

          {/* Brand Compliance Checklist */}
          <div className="bg-white border border-gray-300 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900">Brand Compliance</h3>
              <div className="text-2xl font-bold text-purple-600">
                {getBrandComplianceScore()}%
              </div>
            </div>
            <div className="space-y-3">
              {brandGuidelines.map((guideline) => (
                <div
                  key={guideline.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-6 h-6 rounded-full flex items-center justify-center ${
                        guideline.compliant === true
                          ? 'bg-green-100 text-green-600'
                          : guideline.compliant === false
                          ? 'bg-red-100 text-red-600'
                          : 'bg-gray-200 text-gray-400'
                      }`}
                    >
                      {guideline.compliant === true && <Check className="w-4 h-4" />}
                      {guideline.compliant === false && <AlertCircle className="w-4 h-4" />}
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-900 capitalize">
                        {guideline.type.replace('_', ' ')}
                      </div>
                      <div className="text-xs text-gray-600">{guideline.value}</div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => toggleBrandCompliance(guideline.id, true)}
                      className={`px-3 py-1 rounded text-xs ${
                        guideline.compliant === true
                          ? 'bg-green-600 text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-green-100'
                      }`}
                    >
                      Yes
                    </button>
                    <button
                      onClick={() => toggleBrandCompliance(guideline.id, false)}
                      className={`px-3 py-1 rounded text-xs ${
                        guideline.compliant === false
                          ? 'bg-red-600 text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-red-100'
                      }`}
                    >
                      No
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Design Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Design Notes (Optional)
            </label>
            <textarea
              value={designNotes}
              onChange={(e) => setDesignNotes(e.target.value)}
              rows={4}
              placeholder="Add any additional notes about this design project..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>

          {/* File Preview Grid */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-3">All Design Files Preview</h3>
            <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
              {designFiles.map((file) => (
                <div key={file.id} className="relative">
                  {file.file.type.startsWith('image/') ? (
                    <img
                      src={file.preview}
                      alt={file.file.name}
                      className="w-full h-24 object-cover rounded border border-gray-200"
                    />
                  ) : (
                    <div className="w-full h-24 bg-gray-100 rounded border border-gray-200 flex items-center justify-center">
                      <FileImage className="w-8 h-8 text-gray-400" />
                    </div>
                  )}
                  <div className="text-xs text-gray-600 mt-1 truncate">{file.size.name}</div>
                  <div className="text-xs text-purple-600 font-semibold">{file.format}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Navigation Buttons */}
      <div className="flex justify-between mt-8 pt-6 border-t border-gray-200">
        <button
          onClick={() => setCurrentStep((prev) => Math.max(1, prev - 1))}
          disabled={currentStep === 1}
          className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Previous
        </button>

        {currentStep < 4 ? (
          <button
            onClick={() => setCurrentStep((prev) => prev + 1)}
            disabled={!canProceedToNextStep()}
            className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next Step
          </button>
        ) : (
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isSubmitting ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <Check className="w-5 h-5" />
                Submit for Review
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

export default DesignAssetCreation;
